import React from 'react'
import {useIntl} from 'react-intl'

import {
  AttendanceV2Payload,
  getComprehensiveReportData,
  getReportPeriodLabel,
  hasResultSections,
  isAttendanceResultPayloadKind,
  isEmptyComprehensiveReport,
} from '../../../../modes/attendance/attendanceV2Types'
import AttendanceV2ComprehensiveReportView from './AttendanceV2ComprehensiveReportView'
import AttendanceV2GapNotice from './AttendanceV2GapNotice'
import AttendanceV2ResultView from './AttendanceV2ResultView'
import AttendanceV2SafetyCheckView from './AttendanceV2SafetyCheckView'
import AttendanceV2SectionedResultView from './AttendanceV2SectionedResultView'

type AttendanceV2AnswerBodyProps = {
  payload: AttendanceV2Payload
  theme: 'light' | 'dark'
  tableUnavailableHint?: boolean
}

/** The structured body of a single attendance answer, minus the narrative and the control-flow
 *  prompts. Shared by the top-level message body and by each part of a `compound` answer, so a
 *  sub-request renders exactly like the same question asked on its own. */
const AttendanceV2AnswerBody: React.FC<AttendanceV2AnswerBodyProps> = ({
  payload,
  theme,
  tableUnavailableHint = false,
}) => {
  const intl = useIntl()
  const gapText = payload.kind === 'gap' ? payload.gap || payload.narrative : payload.gap
  const emptyReportPeriod = isEmptyComprehensiveReport(payload)
    ? getReportPeriodLabel(payload)
    : null
  // Caveats the backend attaches when it answered something adjacent to what was asked (e.g. it
  // couldn't resolve the requested class and widened to the user's own classes). Never surfaced
  // before, which made a widened answer look like an exact one.
  const assumptions = payload.data?.assumptions ?? []

  return (
    <>
      {gapText ? <AttendanceV2GapNotice text={gapText} theme={theme} /> : null}

      {assumptions.map((assumption, idx) => (
        <AttendanceV2GapNotice key={idx} text={assumption} theme={theme} />
      ))}

      {payload.kind === 'safety_check' ? (
        <AttendanceV2SafetyCheckView payload={payload} theme={theme} />
      ) : null}

      {emptyReportPeriod ? (
        <AttendanceV2GapNotice
          text={intl.formatMessage(
            {id: 'CHATBOT.ATTENDANCE.REPORT_NO_DATA'},
            {period: emptyReportPeriod}
          )}
          theme={theme}
        />
      ) : null}

      {getComprehensiveReportData(payload) ? (
        <AttendanceV2ComprehensiveReportView payload={payload} theme={theme} />
      ) : isAttendanceResultPayloadKind(payload.kind) ? (
        hasResultSections(payload) ? (
          <AttendanceV2SectionedResultView
            payload={payload}
            theme={theme}
            tableUnavailableHint={tableUnavailableHint}
          />
        ) : (
          <AttendanceV2ResultView
            payload={payload}
            theme={theme}
            tableUnavailableHint={tableUnavailableHint}
          />
        )
      ) : null}
    </>
  )
}

export default AttendanceV2AnswerBody
